import { ROLE_BIB_HR_OPERATIONS_JAKARTA, ROLE_BIB_HR_OPERATIONS_SITE, ROLE_HSE, ROLE_HR, ROLE_MANAGER } from "@/constants/mitraRole";

export const APPROVAL_STATUSES = {
  OPEN: "open",
  APPROVAL_MANAGER: "approval_manager",
  APPROVAL_HSE: "approval_hse",
  APPROVAL_HR: "approval_hr",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export const APPROVAL_STATUS_LABELS = {
  [APPROVAL_STATUSES.APPROVAL_MANAGER]: "Waiting for Manager",
  [APPROVAL_STATUSES.APPROVAL_HSE]: "Waiting for HSE Training",
  [APPROVAL_STATUSES.APPROVAL_HR]: "Waiting for HR Operation Site",
};

export const BIB_APPROVAL_STEP_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "hse", label: "HSE Training" },
  { value: "hr", label: "HR Operation Site" },
];

export const BIB_TNA_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: APPROVAL_STATUSES.APPROVAL_MANAGER, label: APPROVAL_STATUS_LABELS[APPROVAL_STATUSES.APPROVAL_MANAGER] },
  { value: APPROVAL_STATUSES.APPROVAL_HSE, label: APPROVAL_STATUS_LABELS[APPROVAL_STATUSES.APPROVAL_HSE] },
  { value: APPROVAL_STATUSES.APPROVAL_HR, label: APPROVAL_STATUS_LABELS[APPROVAL_STATUSES.APPROVAL_HR] },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const normalizeRoles = (roles) => {
  if (!roles) return [];
  return (Array.isArray(roles) ? roles : [roles]).map((role) => String(role || "").toLowerCase());
};

export const normalizeApprovalStatus = (status) => String(status || "").trim().toLowerCase();

export const getApprovalStatusLabel = (status) => {
  const normalized = normalizeApprovalStatus(status);
  if (!normalized) return "-";
  if (APPROVAL_STATUS_LABELS[normalized]) return APPROVAL_STATUS_LABELS[normalized];

  return normalized
    .split(/[_-]/g)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getTrainingKplhStatus = (item = {}) => {
  const value =
    item?.training?.kplh_status ??
    item?.kplh_status ??
    item?.training?.kplh_matrix ??
    item?.kplh_matrix ??
    null;

  return value == null || value === "" ? null : String(value).toUpperCase();
};

export const isKplhTraining = (item = {}) => getTrainingKplhStatus(item) === "KPLH";

export const usesHseApprovalPath = (item = {}) => {
  const status = normalizeApprovalStatus(item?.status);
  if (status === APPROVAL_STATUSES.APPROVAL_HR) return false;
  if (status === APPROVAL_STATUSES.APPROVAL_HSE) return true;
  return isKplhTraining(item);
};

export const getNextApproverLabel = (item = {}) => {
  const status = normalizeApprovalStatus(item?.status);
  if (status === APPROVAL_STATUSES.APPROVAL_HSE || status === APPROVAL_STATUSES.APPROVAL_HR) {
    return "Approved";
  }
  return usesHseApprovalPath(item) ? "HSE Training" : "HR Operation Site";
};

export const getApprovalStepperSteps = (item = {}) => {
  const nextLabel = getNextApproverLabel(item);
  return [
    { key: "open", label: "Open" },
    { key: APPROVAL_STATUSES.APPROVAL_MANAGER, label: "Manager" },
    {
      key: usesHseApprovalPath(item) ? APPROVAL_STATUSES.APPROVAL_HSE : APPROVAL_STATUSES.APPROVAL_HR,
      label: nextLabel,
    },
    { key: "approved", label: "Approved" },
  ];
};

export const getApprovalStepperIndex = (item = {}) => {
  const status = normalizeApprovalStatus(item?.status);

  if (["approved", "enrolled", "completed", "budgeted"].includes(status)) return 3;
  if (status === APPROVAL_STATUSES.APPROVAL_HSE || status === APPROVAL_STATUSES.APPROVAL_HR) return 2;
  if (status === APPROVAL_STATUSES.APPROVAL_MANAGER) return 1;
  if (status === APPROVAL_STATUSES.REJECTED) {
    return usesHseApprovalPath(item) ? 2 : 1;
  }
  return 0;
};

export const getInboxStatusesForRoles = (roles) => {
  const normalized = normalizeRoles(roles);
  const statuses = [];
  if (normalized.includes(ROLE_HSE)) statuses.push(APPROVAL_STATUSES.APPROVAL_HSE);
  if (
    normalized.includes(ROLE_BIB_HR_OPERATIONS_SITE) ||
    normalized.includes(ROLE_BIB_HR_OPERATIONS_JAKARTA) ||
    normalized.includes(ROLE_HR)
  ) {
    statuses.push(APPROVAL_STATUSES.APPROVAL_HR);
  }
  if (normalized.includes(ROLE_MANAGER)) statuses.push(APPROVAL_STATUSES.APPROVAL_MANAGER);
  return statuses;
};

export const isApprovalActionable = (item = {}, roles) => {
  const inboxStatuses = getInboxStatusesForRoles(roles);
  const matchesInbox = inboxStatuses.length
    ? inboxStatuses.includes(normalizeApprovalStatus(item?.status))
    : !["approved", "rejected", "completed", "budgeted", "cancelled", "canceled"].includes(
        normalizeApprovalStatus(item?.status)
      );

  if (typeof item?.is_actionable === "boolean") {
    return item.is_actionable && matchesInbox;
  }

  return matchesInbox;
};
